/**
 * PDF sidecar regeneration — re-renders each study's PDF and re-uploads through
 * the storage router. Sidecars aren't part of the persistence envelope, so they
 * don't flow through migration or read-repair; this is the cloud-backfill path
 * for studies that never hit `writeStudies` (e.g. migrated, bulk-imported).
 *
 * Sequential — PDF rendering is CPU-bound; parallel calls starve the main thread.
 */

import type { Study } from '@skjv/common';
import { getStorageRouter } from '@skjv/storage';
import { studyFileBaseName, studyFolderName } from './study-file-name';
import { studyMarkdownSidecar } from './study-sidecars';

export interface PdfRegenerateProgress {
  studyIndex: number;
  totalStudies: number;
  studyName: string;
  generated: number;
  errors: number;
}

export interface PdfRegenerateError {
  studyId: string;
  studyName: string;
  reason: string;
}

export interface PdfRegenerateResult {
  generated: number;
  errors: PdfRegenerateError[];
}

export interface PdfRegenerateOptions {
  onProgress?: (progress: PdfRegenerateProgress) => void;
  signal?: AbortSignal;
}

export async function regenerateStudyPdfs(
  studies: Study[],
  opts: PdfRegenerateOptions = {},
): Promise<PdfRegenerateResult> {
  const router = getStorageRouter();
  const { generateStudyPdf } = await import('@/services/study-pdf');
  const errors: PdfRegenerateError[] = [];
  let generated = 0;

  for (let i = 0; i < studies.length; i++) {
    if (opts.signal?.aborted) break;
    const study = studies[i];
    opts.onProgress?.({
      studyIndex: i,
      totalStudies: studies.length,
      studyName: study.name,
      generated,
      errors: errors.length,
    });

    try {
      const pdf = await generateStudyPdf(study);
      const markdown = studyMarkdownSidecar(study);
      await router.putRecord<Study>({
        namespace: 'studies',
        key: study.id,
        value: study,
        updatedAt: study.updatedAt ?? new Date().toISOString(),
        displayName: studyFolderName(study),
        recordFileName: `${studyFileBaseName(study)}.json`,
        sidecars: [
          ...(markdown ? [markdown] : []),
          {
            name: `${studyFileBaseName(study)}.pdf`,
            blob: pdf,
            contentType: 'application/pdf',
          },
        ],
      });
      generated++;
    } catch (err) {
      errors.push({
        studyId: study.id,
        studyName: study.name,
        reason: err instanceof Error ? err.message : String(err),
      });
    }
  }

  opts.onProgress?.({
    studyIndex: studies.length,
    totalStudies: studies.length,
    studyName: '',
    generated,
    errors: errors.length,
  });

  return { generated, errors };
}
